<template>
	<UserPageWrapper>
		<Title>{{ $t("seo.pages.followed-companies.title") }}</Title>
		<Meta name="description" :content="$t('seo.pages.followed-companies.description')" />
		<Link rel="canonical" :href="`https://${globalConfig.host}${$route.fullPath}`" />
		<Link v-if="globalConfig.isBlue" rel="icon" type="image/x-icon" href="/blue-favicon.ico?v3" />
		<Link v-else rel="icon" type="image/x-icon" href="/favicon.ico?v3" />
		<Meta property="og:url" :content="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:type" content="website" />
		<Meta
			property="og:locale"
			:content="`${$i18n.locale}_${$i18n.locale == 'en' ? 'US' : $i18n.locale.toUpperCase()}`"
		/>
		<Meta property="og:title" :content="$t('seo.pages.followed-companies.title')" />
		<Meta property="og:description" :content="$t('seo.pages.followed-companies.description')" />
		<Meta
			property="og:image"
			content="https://res.cloudinary.com/djbs/image/upload/v1669288084/content/DreamJobs/Graphics/ogimage_dreamjobs.png"
		/>
		<Meta property="og:image:type" content="image/jpeg" />
		<Meta property="og:image:width" content="1200" />
		<Meta property="og:image:height" content="628" />

		<AdaptiveContainer class="flex flex-col gap-0 mb-16">
			<BaseHeading type="h2" class="leading-3 mb-4">
				<span class="text-2xl md:text-3xl">{{ $t("user.followedCompanies.title") }}</span>
			</BaseHeading>

			<p v-html="$t('user.followedCompanies.info')"></p>
		</AdaptiveContainer>

		<AdaptiveContainer>
			<template v-if="loading || !isAuth">
				<DjCardCompany :loading="true" />
			</template>
			<template v-else-if="lovedCompanies && isAuth">
				<template v-for="(company, index) in lovedCompanies" :key="index">
					<CardCompany
						:company="company"
						:langs="langs"
						:locale="locale"
						@on-click="$router.push(`/company/${$event}`)"
					/>
				</template>
			</template>
		</AdaptiveContainer>
		<ResponsiveContainer class="flex mt-8">
			<BasePagination
				v-if="lovedCompanies.length > 0 && !loading && paginationLastPage > 1"
				v-model="paginationPage"
				:total="paginationLastPage"
				class="mx-auto mb-10"
				@change-page="setPage"
				:next-lang="$t('paginationNext')"
				:prev-lang="$t('paginationPrev')"
			/>
		</ResponsiveContainer>

		<AdaptiveContainer class="pb-20">
			<p v-if="lovedCompanies.length < 1 && !loading" class="italic">
				{{ $t("user.followedCompanies.noCompanies") }}
			</p>
		</AdaptiveContainer>
	</UserPageWrapper>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import { SavedCompaniesRepository } from "~/repositories/SavedCompaniesRepository";
import { CompanyDto } from "~~/types/company";

export default defineComponent({
	name: "FollowedCompaniesPage",
	setup() {
		const savedCompaniesRepository = new SavedCompaniesRepository();

		definePageMeta({
			middleware: ["auth"],
		});

		const { t, locale } = useI18n();
		const langs = useLangs.job;
		const loading = ref(true);
		const isAuth = useState("isAuth");

		const lovedCompanies = ref<CompanyDto[]>([]);
		const paginationPage = ref(1);
		const paginationLastPage = ref(1);

		async function getLovedCompanies(page = 1) {
			loading.value = true;
			const response = await savedCompaniesRepository.getSavedCompanies(page);
			lovedCompanies.value = response.data;
			paginationLastPage.value = response.last_page;
			paginationPage.value = response.current_page;
			loading.value = false;
		}

		function setPage(page: number) {
			getLovedCompanies(page);
		}

		watch(isAuth, (val: boolean) => {
			val == true && getLovedCompanies();
		});

		onMounted(() => {
			isAuth.value == true && getLovedCompanies();
		});

		return {
			loading,
			t,
			locale,
			langs,
			isAuth,
			lovedCompanies,
			paginationPage,
			paginationLastPage,
			setPage,
		};
	},
});
</script>
